<template>
  <div
    class="container"
    :class="[
      [FlipCoinTabs.BetSelection, FlipCoinTabs.Game, FlipCoinTabs.Result].includes(activeTab) || isRewind
        ? 'gameStartedContainer'
        : null,
      activeTab === FlipCoinTabs.Info ? 'infoStepActive' : null
    ]"
  >
    <div
      v-if="startedGameId || isRewind"
      class="skip-animation-area"
      :class="{
        'skip-animation-area-full-height': isRewind,
        'skip-animation-area-mobile': $device.isMobile
      }"
      @click="handleClick"
    />
    <div v-if="!isRewind" class="wrapperContainer">
      <p
        v-if="
          activeTab === FlipCoinTabs.Bets ||
          activeTab === FlipCoinTabs.SelectToken ||
          activeTab === FlipCoinTabs.Winners
        "
        class="title"
      >
        {{ $t(`flipCoinTooltip${activeTab}`) }}
      </p>
      <div
        class="settingsBar"
        :style="{ justifyContent: activeTab === FlipCoinTabs.Winners ? 'flex-end' : 'space-between' }"
      >
        <div v-if="activeTab === FlipCoinTabs.Bets && !showLoginRequired" class="totalSPInfo">
          <p v-if="!$device.isMobile">
            <span>{{ $t('flipCoinTotalBets') }} </span
            ><span class="amount">{{ flipCoinSocket.parsedData?.length }}</span>
          </p>
          <span v-if="!$device.isMobile">|</span>
          <p>
            <span v-if="!$device.isMobile">{{ $t('flipCoinYourBets') }}</span>
            <span class="amount"> + {{ maxPointsForAllBetsForCurrentUser }} </span>
            <img src="/img/tokens/seasonPoints.png" alt="points" />
          </p>
        </div>

        <CustomDropdown
          v-if="activeTab === FlipCoinTabs.Bets && !showLoginRequired"
          class="dropdown"
          :class="anyModalActive ? 'opaque' : null"
          :options="sortOptions"
          :default-value="currentSortOption"
          @handle-change="dropdownChangeHandler"
        >
          <template #listItem="slotProps">
            <p>{{ $t(SortOptionsLabels[slotProps.option?.toString() as SortOptions] || '') }}</p>
          </template>
        </CustomDropdown>

        <el-checkbox
          v-if="activeTab === FlipCoinTabs.Winners"
          v-model="showOwn"
          class="isOwnCheckbox"
          fon
          :style="{
            '--el-checkbox-checked-bg-color': '#00A906',
            '--el-checkbox-bg-color': '#A49686'
          }"
          >{{ $t('showOwnGames') }}
        </el-checkbox>
      </div>

      <component
        :is="activeComponent"
        :exchange-token-callback="setExchangeToken"
        :exchange-token="exchangeToken"
        :game-data="gameData"
        :loading-game-id="startedGameId && !gameData?.joinerPlayer.userAddress ? startedGameId : null"
        @cancel-game="cancelGameHandler"
        @set-token-game-data="setBlockchainPayloadHandler"
        @set-odd="setOddHandler"
        @confirm-bet-handler="joinGameHandler"
        @go-to-stats="goToStats"
        @get-out="getOut"
        @close-bet-selection="backToTokensList"
        @back-to-bets="showBets"
        @start-flipcoin-animation="handleRewindGame"
        @update-points="(amount: number) => (maxPointsForAllBetsForCurrentUser = amount)"
      />

      <TavernFlipCoinBigButton
        v-if="activeTab === FlipCoinTabs.Bets && !showLoginRequired"
        :label="$t('tavernPlaceBet')"
        class="tw:mt-4! placeBetBtn"
        :class="anyModalActive ? 'opaque' : null"
        :style="{ flexShrink: $device.isMobile ? 1 : 0, minHeight: $device.isMobile ? '50px' : 'unset' }"
        @click="startNewGameHandler"
      ></TavernFlipCoinBigButton>
    </div>
    <TavernFlipCoinSteps
      v-if="!isRewind"
      :show-login-required="!!address"
      :active="activeTab"
      :set-active-callback="setActiveStep"
    />
  </div>
</template>
<script lang="ts" setup>
import { FlipCoin__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import type { IFlipCoinTokenConfig, IUserModel, TFlipCoinInitGamePayload } from '~/types/apiService';
import { GameStatus, type FlipCoinGame, type StartGameDTO, FlipCoinTabs } from '~/types/FlipCoin';
import { ACTION_REJECTED, SortOptions, SortOptionsLabels } from '~/utils/constants';
import { useWalletInfo, useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { apiUrls } from '~/utils/constants';
import { formatEther } from 'ethers';
import { WebSocketsKeys } from '~/types/common';

const sortOptions = Object.values(SortOptions);

const { apiUrl, blockchain, flipCoinContract } = useEnvs();
const { $websockets: websockets } = useNuxtApp();
const flipCoinSocket = websockets?.[WebSocketsKeys.flipCoinGame];

const { $errorHandler } = useNuxtApp();
const { cancelGame, startGame, joinGame } = useFlipCoinMethods();
const { availableTokens } = useTokensReader();
const { getProvider } = useProvider();
const { t } = useI18n();
const emit = defineEmits(['start-flipcoin-animation', 'show-overlay', 'close-game', 'skip-animation']);
const { walletInfo } = useWalletInfo();

const contract = FlipCoin__factory.connect(flipCoinContract, getProvider());

const TavernFlipCoinBetsTable = resolveComponent('TavernFlipCoinBetsTable');
const TavernTokensList = resolveComponent('TavernTokensList');
const TavernFlipCoinBetSelection = resolveComponent('TavernFlipCoinBetSelection');
const TavernFlipCoinGameStep = resolveComponent('TavernFlipCoinGameStep');
const TavernFlipCoinStatsTable = resolveComponent('TavernFlipCoinStatsTable');
const TavernFlipCoinInfoModal = resolveComponent('TavernFlipCoinInfoModal');

const isRewind = ref(false);
const areAnimationsFinished = inject<Ref<boolean>>('animationsFinished');
const activeTab = ref(FlipCoinTabs.Bets);
const exchangeToken = ref<IFlipCoinTokenConfig | null>(null);
const gameData = ref<FlipCoinGame | null>(null);
const startedGameId = ref<number | null>(null);
const showOwn = ref(false);
const currentSortOption = ref(SortOptions.DATE);
const showLoginRequired = ref(false);
const showConfirmBet = ref(false);
const showCancelBet = ref(false);
const showTakenBet = ref(false);
const anyModalActive = ref(false);
const maxPointsForAllBetsForCurrentUser = ref(0);
const { address } = useWeb3ModalAccount();
const store = useMainStore();

const blockchainPayload = ref<StartGameDTO>({
  isOdd: false,
  playAmount: 0,
  token: ''
});

const { data: userData } = useNuxtData<IUserModel>('userData');

provide('currentSortOption', currentSortOption);
provide('showOwn', showOwn);
provide('showLoginRequired', showLoginRequired);
provide('showConfirmBet', showConfirmBet);
provide('showCancelBet', showCancelBet);
provide('showTakenBet', showTakenBet);
provide('anyModalActive', anyModalActive);

const dropdownChangeHandler = (value: SortOptions) => {
  currentSortOption.value = value;
};

watch(activeTab, () => {
  if (activeTab.value === FlipCoinTabs.BetSelection || activeTab.value === FlipCoinTabs.Result) {
    emit('show-overlay', true);
    return;
  }

  emit('show-overlay', false);
});

watch([areAnimationsFinished], () => {
  if (!areAnimationsFinished?.value) return;

  if (!isRewind.value) {
    startedGameId.value = null;
    activeTab.value = FlipCoinTabs.Result;
  } else {
    isRewind.value = false;
  }
});

watch(
  () => flipCoinSocket.parsedData,
  (newGamesData: FlipCoinGame[], oldGamesData: FlipCoinGame[]) => {
    handleGameStart(newGamesData, oldGamesData);
  }
);

watch(
  [showLoginRequired, showConfirmBet, showCancelBet, showTakenBet],
  ([newShowLogin, newShowConfirmBet, newShowCancelBet, newShowTakenBet]) => {
    anyModalActive.value = [newShowLogin, newShowConfirmBet, newShowCancelBet, newShowTakenBet].some((val) => val);

    emit('show-overlay', anyModalActive.value);
  },
  { immediate: false }
);

const activeComponent = computed(() => {
  switch (activeTab.value) {
    case FlipCoinTabs.Bets:
      return TavernFlipCoinBetsTable;
    case FlipCoinTabs.SelectToken:
      return TavernTokensList;
    case FlipCoinTabs.BetSelection:
      return TavernFlipCoinBetSelection;
    case FlipCoinTabs.Game:
    case FlipCoinTabs.Result:
      return TavernFlipCoinGameStep;
    case FlipCoinTabs.Winners:
      return TavernFlipCoinStatsTable;
    case FlipCoinTabs.Info:
      return TavernFlipCoinInfoModal;
    default:
      return null;
  }
});

const handleClick = () => {
  if (isRewind.value) {
    emit('skip-animation');
    isRewind.value = false;
    return;
  }

  if (activeTab.value !== FlipCoinTabs.Game) {
    return;
  }

  emit('skip-animation');
  activeTab.value = FlipCoinTabs.Bets;
  gameData.value = null;
  startedGameId.value = null;
};

function handleGameStart(newGamesData: FlipCoinGame[], oldGamesData: FlipCoinGame[]) {
  const idlePlayerGame = getIdlePlayerGame(newGamesData, oldGamesData);

  if (!idlePlayerGame) return;

  saveHashToLocalStorage(t('flipCoinBetAccepted'), '');
  store.updateVersion();

  gameData.value = idlePlayerGame;
  startedGameId.value = idlePlayerGame.gameId;
  activeTab.value = FlipCoinTabs.Game;
  emit('start-flipcoin-animation', idlePlayerGame);

  modifyHistoryHashStatus('', 'Done');
  store.updateVersion();
}

function getIdlePlayerGame(newGames: FlipCoinGame[], oldGames: FlipCoinGame[]): FlipCoinGame | undefined {
  if (!oldGames) return;

  const oldGamesMap = new Map<number, FlipCoinGame>();

  for (const game of oldGames) {
    oldGamesMap.set(game.gameId, game);
  }

  const idlePlayerGame = newGames.find((newGame: FlipCoinGame) => {
    const oldGame = oldGamesMap.get(newGame.gameId);

    return (
      oldGame &&
      oldGame.ownerPlayer.userAddress.toLowerCase() === newGame.ownerPlayer.userAddress.toLowerCase() &&
      oldGame.ownerPlayer.userAddress.toLowerCase() === address.value?.toLowerCase() &&
      oldGame.status !== newGame.status &&
      newGame.status !== GameStatus.CANCELED &&
      newGame.status !== GameStatus.STARTED &&
      !(oldGame.status === GameStatus.STARTED && newGame.status === GameStatus.PENDING)
    );
  });

  return idlePlayerGame;
}

const backToTokensList = () => {
  activeTab.value = FlipCoinTabs.SelectToken;
};

const handleRewindGame = (game: FlipCoinGame) => {
  isRewind.value = true;
  emit('start-flipcoin-animation', game);
};

const cancelGameHandler = async (gameId: number) => {
  const txResponse = await cancelGame(gameId);

  if (!txResponse?.hash) return;

  saveHashToLocalStorage(t('flipCoinGameCancelled'), txResponse?.hash);
  store.updateVersion();

  txResponse?.wait();

  modifyHistoryHashStatus(txResponse?.hash, 'Done');
  store.updateVersion();
};

const setBlockchainPayloadHandler = ({ token, playAmount }: StartGameDTO) => {
  blockchainPayload.value = { ...blockchainPayload.value, token, playAmount };
};

const setOddHandler = async (isOdd: boolean) => {
  blockchainPayload.value = {
    ...blockchainPayload.value,
    isOdd
  };

  activeTab.value = FlipCoinTabs.Bets;

  const txResponse = await startGame(blockchainPayload.value);

  if (!txResponse?.hash) return;

  $fetch<TFlipCoinInitGamePayload>(apiUrls.flipCoinGame.initGame, {
    method: 'POST',
    baseURL: apiUrl,
    body: {
      ownerPlayer: address.value,
      tokenAddress: blockchainPayload.value.token,
      transactionHash: txResponse.hash,
      isOddBet: isOdd,
      betAmount: blockchainPayload.value.playAmount
    } as TFlipCoinInitGamePayload
  });

  const { playAmount, token } = blockchainPayload.value;
  const tokenName = tokenMetaData(blockchain.contracts, token).methodName;
  saveHashToLocalStorage(t('flipCoinGameBetPlaced', { amount: playAmount, token: tokenName }), txResponse?.hash);
  store.updateVersion();

  txResponse?.wait();

  modifyHistoryHashStatus(txResponse?.hash, 'Done');
  store.updateVersion();
};

const setExchangeToken = (token: IFlipCoinTokenConfig) => {
  exchangeToken.value = token;
  activeTab.value = FlipCoinTabs.BetSelection;
};

const showBets = () => {
  showLoginRequired.value = false;
};

const startNewGameHandler = () => {
  if (!address.value) {
    showLoginRequired.value = true;
    return;
  }

  activeTab.value = FlipCoinTabs.SelectToken;
};

const goToStats = () => {
  activeTab.value = FlipCoinTabs.Winners;
};

const getOut = () => {
  activeTab.value = activeTab.value === FlipCoinTabs.BetSelection ? FlipCoinTabs.SelectToken : FlipCoinTabs.Bets;
  gameData.value = null;
};

const setActiveStep = (step: number) => {
  if (startedGameId.value) {
    return;
  }
  if ([FlipCoinTabs.BetSelection, FlipCoinTabs.Game, FlipCoinTabs.Result].includes(step)) {
    return;
  }

  if (FlipCoinTabs.SelectToken && showLoginRequired.value) {
    return;
  }

  if (step === FlipCoinTabs.SelectGame) {
    emit('close-game');
    return;
  }

  activeTab.value = step;
};

const joinGameHandler = async (gameDataArg: FlipCoinGame) => {
  if (!address.value) {
    showLoginRequired.value = true;
    return;
  }

  const { betAmount, tokenAddress, gameId } = gameDataArg;

  const availableTokenAmount = await availableTokens(address.value, tokenAddress);

  if (Number(availableTokenAmount) < Number(betAmount)) {
    ElNotification.error(t('flipCoinNotEnoughTokens'));
    showLoginRequired.value = false;
    return;
  }

  const txResponse = await joinGame(gameId).catch((er) => {
    if ((er as { code: string })?.code === ACTION_REJECTED) {
      const errorMessage = getCancelErrorMessage(er, walletInfo.value?.name!);
      $errorHandler(t(errorMessage));
      return;
    }

    showTakenBet.value = true;
    startedGameId.value = null;
  });

  if (!txResponse?.hash) {
    return;
  }

  const games = flipCoinSocket.parsedData;
  const game = games.find((game: FlipCoinGame) => game.gameId === gameId) as FlipCoinGame;
  saveHashToLocalStorage(t('flipCoinGameStarted'), txResponse?.hash);
  store.updateVersion();

  txResponse
    ?.wait()
    .then(async () => {
      const winnerData = await contract.getWinner(gameId);

      const gameToStart = {
        ...game,
        winner: winnerData[0],
        winAmount: formatEther(winnerData[1]),
        joinerPlayer: {
          userName: userData.value?.user.name || '',
          userAvatarUrl: userData.value?.user.avatarUrl || '',
          userAddress: address.value || ''
        }
      };

      gameData.value = gameToStart;
      activeTab.value = FlipCoinTabs.Game;
      emit('start-flipcoin-animation', gameToStart);
    })
    .catch(() => {
      showTakenBet.value = true;
      startedGameId.value = null;
      return;
    });

  startedGameId.value = gameId;

  modifyHistoryHashStatus(txResponse?.hash, 'Done');
  store.updateVersion();

  startedGameId.value = gameDataArg.gameId;
};
</script>

<style lang="scss" scoped>
.container {
  background-color: rgba(26, 2, 2, 0.2);
  position: relative;
}

.skip-animation-area {
  position: absolute;
  cursor: pointer;
  width: 100%;
  height: 70%;
}

.skip-animation-area.skip-animation-area-full-height {
  height: 100%;
}

.skip-animation-area-mobile {
  height: calc(100dvh - 256px);
}

.infoStepActive {
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
}

.gameStartedContainer {
  background-color: unset;

  .wrapperContainer {
    background: unset;
    backdrop-filter: unset;
    padding: unset;
  }
}

.opaque {
  opacity: 0.2;
  pointer-events: none;
}

.settingsBar {
  gap: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 5px;
  margin-bottom: 5px;
  width: 100%;

  @media screen and (max-width: 479px) {
    & > * {
      width: 100%;
    }
  }
}

.wrapperContainer {
  height: 100%;
  padding: 15px 25px 64px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(2px);
  border-radius: 14px;

  :deep(.dropdown) {
    background: rgba(50, 31, 25, 1);
    // position: absolute;
    // top: 20px;
    // right: 50px;
    width: max-content;
    min-width: 200px;
    border-color: rgba(72, 59, 32, 1);
    border-radius: 10px;
    font-size: 16px;
    font-weight: 500;
    font-family: Eczar, sans-serif;
    z-index: 9999;

    @media screen and (max-width: 479px) {
      position: relative;
      width: 100%;
      max-width: 50%;
      top: 0;
      left: 0;
    }

    &.expanded {
      border-radius: 10px 10px 0 0;
    }

    .menuList {
      border: 2px solid rgba(72, 59, 32, 1);
      top: 28px;
    }

    .checkedItem {
      padding: 0 16px;
      height: 30px;
      color: #fff;
      img {
        width: 16px;
      }
    }

    .menuListItem {
      padding: 0 16px;
      height: 30px;
      background: rgba(50, 31, 25, 1);
      color: #a49686;

      &:not(:last-child) {
        border-bottom-width: 0;
      }
    }
  }
}

.isOwnCheckbox {
  justify-content: flex-end;
  max-width: 100%;
  align-items: center;

  :deep(.el-checkbox__label) {
    color: #fff;
    font-size: 18px;
    font-family: Eczar;
    font-weight: 500;
  }

  :deep(.el-checkbox__inner) {
    border-color: #fff;
  }
  :deep(.el-checkbox__input) {
    margin-top: -3px;
  }
}

.title {
  margin: 0 auto;
  font-size: 30px;
  font-weight: 700;
  margin-bottom: 15px;
  color: #fff;
  font-family: Grenze, sans-serif;
  text-align: center;
}

.totalSPInfo {
  // @media screen and (max-width: 479px) {
  //   position: absolute;
  //   top: 20px;
  //   left: 20px;
  // }
  padding: 3px 9px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  border: 1px solid #201a0e;
  border-radius: 12px;
  background-color: #07000080;
  gap: 10px;
  font-family: Eczar, sans-serif;
  font-size: 16px;
  color: #a2a2a2;
  font-weight: 500;

  @media screen and (max-width: 479px) {
    left: 5px;
    &.right-mobile {
      left: unset;
      right: 5px;
    }
  }

  .amount {
    color: #fff;
    white-space: nowrap;
  }

  img {
    width: 26px;
  }

  & > p {
    display: flex;
    align-items: center;
    gap: 5px;
  }
}

.placeBetBtn {
  @media screen and (max-width: 479px) {
    margin-bottom: 14px;
  }
}
</style>
